import React from "react";
import { visaCountries, regionList, visaTypeList, passportCountries, passportValidityRules } from "./homeData/steps";
import { matchesCountryString } from "../../../utils/countrySearch";

// ── Speedometer constants (inline — mirrors speedo.js for self-containment) ──
const SPEEDO_CX = 200;
const SPEEDO_CY = 200;
const SPEEDO_R = 148;
const SPEEDO_START_ANGLE = -135;
const SPEEDO_SWEEP = 270;
const SPEEDO_STROKE_WIDTH = 20;
const SPEEDO_DEFAULT_VALUE = 80;
const SPEEDO_ANIM_DELAY = 350;

const SPEEDO_TICK_LABELS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startAngle, endAngle) {
  const s = polarToCartesian(cx, cy, r, endAngle);
  const e = polarToCartesian(cx, cy, r, startAngle);
  const large = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 0 ${e.x} ${e.y}`;
}

function valueToAngle(v) {
  return SPEEDO_START_ANGLE + (Math.min(100, Math.max(0, v)) / 100) * SPEEDO_SWEEP;
}

function valueToNeedleDeg(v) {
  return valueToAngle(v);
}

/* ══════════════════════════════════════════════
   SPEEDOMETER COMPONENT
══════════════════════════════════════════════ */
function SpeedometerGauge({ targetValue = SPEEDO_DEFAULT_VALUE }) {
  const [displayValue, setDisplayValue] = React.useState(0);
  const [animStarted, setAnimStarted] = React.useState(false);
  const [needleDeg, setNeedleDeg] = React.useState(SPEEDO_START_ANGLE);
  const gaugeRef = React.useRef(null);
  const animFrameRef = React.useRef(null);
  const counterRef = React.useRef(null);

  const runAnimation = React.useCallback(() => {
    setDisplayValue(0);
    setNeedleDeg(SPEEDO_START_ANGLE);
    setAnimStarted(false);

    // Small delay then trigger CSS transition + counter
    setTimeout(() => {
      setAnimStarted(true);
      setNeedleDeg(valueToNeedleDeg(targetValue));

      // Animate counter
      const duration = 1700;
      const startTime = performance.now();
      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // easeOutExpo
        const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        setDisplayValue(Math.round(eased * targetValue));
        if (progress < 1) counterRef.current = requestAnimationFrame(tick);
      };
      counterRef.current = requestAnimationFrame(tick);
    }, SPEEDO_ANIM_DELAY);
  }, [targetValue]);

  // IntersectionObserver — fire animation each time it enters viewport
  React.useEffect(() => {
    const el = gaugeRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          runAnimation();
        } else {
          // Reset when out of view so it replays on re-entry
          setAnimStarted(false);
          setDisplayValue(0);
          setNeedleDeg(SPEEDO_START_ANGLE);
          cancelAnimationFrame(counterRef.current);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(counterRef.current);
    };
  }, [runAnimation]);

  // Also re-run on each mount (handles refresh / route change)
  React.useEffect(() => {
    return () => cancelAnimationFrame(counterRef.current);
  }, []);

  // ── Ticks ──
  const ticks = [];
  const totalTicks = 50;
  for (let i = 0; i <= totalTicks; i++) {
    const pct = i / totalTicks;
    const angle = SPEEDO_START_ANGLE + pct * SPEEDO_SWEEP;
    const isMajor = i % 5 === 0;
    const innerR = SPEEDO_R - (isMajor ? 16 : 8);
    const outerR = SPEEDO_R - 2;
    const p1 = polarToCartesian(SPEEDO_CX, SPEEDO_CY, innerR, angle);
    const p2 = polarToCartesian(SPEEDO_CX, SPEEDO_CY, outerR, angle);
    ticks.push(
      <line
        key={i}
        x1={p1.x} y1={p1.y}
        x2={p2.x} y2={p2.y}
        stroke={isMajor ? "rgba(148,185,255,0.65)" : "rgba(148,185,255,0.25)"}
        strokeWidth={isMajor ? 2 : 1}
        strokeLinecap="round"
      />
    );
  }

  // ── Number labels ──
  const numLabels = SPEEDO_TICK_LABELS.map((val) => {
    const angle = valueToAngle(val);
    const pos = polarToCartesian(SPEEDO_CX, SPEEDO_CY, SPEEDO_R - 36, angle);
    return (
      <text
        key={val}
        x={pos.x}
        y={pos.y}
        className="speedo-num"
      >
        {val}
      </text>
    );
  });

  // ── Zone arc segments ──
  const zones = [
    { from: 0,  to: 33,  startColor: "#0a1952", endColor: "#1a4fc4" },
    { from: 33, to: 66,  startColor: "#1a4fc4", endColor: "#3a7bd5" },
    { from: 66, to: 100, startColor: "#3a7bd5", endColor: "#6db3f2" },
  ];

  const zoneArcs = zones.map((z, i) => {
    const sa = valueToAngle(z.from);
    const ea = valueToAngle(z.to);
    // Convert angles to strokeDasharray for arc
    const d = arcPath(SPEEDO_CX, SPEEDO_CY, SPEEDO_R, sa, ea);
    return (
      <path
        key={i}
        d={d}
        fill="none"
        stroke={`url(#zoneGrad${i})`}
        strokeWidth={SPEEDO_STROKE_WIDTH}
        strokeLinecap="round"
        opacity={0.85}
      />
    );
  });

  // ── Needle ──
  const needleLen = SPEEDO_R - 28;
  const needleTip = polarToCartesian(SPEEDO_CX, SPEEDO_CY, needleLen, 0); // angle=0 relative, CSS rotates
  const needleBase1 = { x: SPEEDO_CX - 5, y: SPEEDO_CY + 6 };
  const needleBase2 = { x: SPEEDO_CX + 5, y: SPEEDO_CY + 6 };
  const needleTipPt = { x: SPEEDO_CX, y: SPEEDO_CY - needleLen };

  const needleStyle = {
    transformOrigin: `${SPEEDO_CX}px ${SPEEDO_CY}px`,
    transform: `rotate(${needleDeg}deg)`,
    transition: animStarted
      ? "transform 1.7s cubic-bezier(0.34, 1.56, 0.64, 1)"
      : "none",
  };

  return (
    <div className="speedo-wrap" ref={gaugeRef}>
      {/* Title */}
      {/* <div className="speedo-title-strip">
        <div className="speedo-title-dot" />
        <span className="speedo-title-text">Visa Approval Index</span>
        <div className="speedo-title-dot" />
      </div> */}

      <div style={{ position: "relative", display: "inline-block" }}>
        <svg
          className="speedo-svg"
          viewBox="0 60 400 230"
          width="420"
          height="210"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Zone gradients */}
            <linearGradient id="zoneGrad0" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#0a1952" />
              <stop offset="100%" stopColor="#1a4fc4" />
            </linearGradient>
            <linearGradient id="zoneGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#1a4fc4" />
              <stop offset="100%" stopColor="#3a7bd5" />
            </linearGradient>
            <linearGradient id="zoneGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#3a7bd5" />
              <stop offset="100%" stopColor="#6db3f2" />
            </linearGradient>
            {/* Needle gradient */}
            <linearGradient id="needleGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"   stopColor="#6db3f2" />
              <stop offset="100%" stopColor="#0d2d87" />
            </linearGradient>
            {/* Cap gradient */}
            <radialGradient id="capGrad" cx="40%" cy="35%" r="60%">
              <stop offset="0%"   stopColor="#4a90d9" />
              <stop offset="100%" stopColor="#0a1952" />
            </radialGradient>
            {/* Glow filter */}
            <filter id="needleGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur" />
              <feFlood floodColor="#3a7bd5" floodOpacity="0.6" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="shadow" />
              <feMerge><feMergeNode in="shadow" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="arcGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Track (background arc) */}
          <path
            d={arcPath(SPEEDO_CX, SPEEDO_CY, SPEEDO_R, SPEEDO_START_ANGLE, SPEEDO_START_ANGLE + SPEEDO_SWEEP)}
            fill="none"
            stroke="rgba(10,25,80,0.35)"
            strokeWidth={SPEEDO_STROKE_WIDTH + 4}
            strokeLinecap="round"
          />

          {/* Zone colored arcs */}
          <g filter="url(#arcGlow)">{zoneArcs}</g>

          {/* Ticks */}
          <g>{ticks}</g>

          {/* Number labels */}
          <g>{numLabels}</g>

          {/* Needle */}
          <g style={needleStyle} filter="url(#needleGlow)">
            {/* Shaft */}
            <polygon
              points={`${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y} ${needleTipPt.x},${needleTipPt.y}`}
              fill="url(#needleGrad)"
            />
          </g>

          {/* Center cap */}
          <circle cx={SPEEDO_CX} cy={SPEEDO_CY} r={14} fill="#09122a" stroke="rgba(100,160,255,0.4)" strokeWidth={2} />
          <circle cx={SPEEDO_CX} cy={SPEEDO_CY} r={8}  fill="url(#capGrad)" />
          <circle cx={SPEEDO_CX} cy={SPEEDO_CY} r={3}  fill="#6db3f2" opacity={0.9} />

          {/* Center value display */}
          <text
            x={SPEEDO_CX}
            y={SPEEDO_CY + 52}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="'DM Mono', 'Fira Code', monospace"
            fontSize="32"
            fontWeight="700"
            fill="rgba(17, 56, 107, 0.8)"
            style={{ filter: "drop-shadow(0 0 10px rgba(80,150,255,0.55))" }}
          >
            {displayValue}
            <tspan fontSize="16" fill="rgba(17, 56, 107, 0.8)">%</tspan>
          </text>
          <text
            x={SPEEDO_CX}
            y={SPEEDO_CY + 75}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="'DM Mono', monospace"
            fontSize="15"
            letterSpacing="1"
            fill="rgba(12, 52, 111, 0.55)"
          >
            APPROVAL INDEX
          </text>
        </svg>
      </div>

      {/* Zone labels */}
      <div className="speedo-zone-labels">
        <span className="speedo-zone-label speedo-zone-label--low">Low</span>
        <span className="speedo-zone-label speedo-zone-label--mid">Medium</span>
        <span className="speedo-zone-label speedo-zone-label--high">High</span>
      </div>
    </div>
  );
}

/* ─── tiny helpers ─── */
const pad2 = (n) => String(n).padStart(2, "0");
const CIRC = 339.3;
const getMeterOffset = (pct) => CIRC - (CIRC * pct) / 100;

const getFactorLabel = (id) => {
  const map = {
    purpose: "Purpose of visit",
    finances: "Financial sufficiency",
    travel_history: "Travel history",
    employment: "Employment stability",
    home_ties: "Ties to home country",
    duration: "Duration of stay",
    accommodation: "Accommodation",
    refusals: "Prior visa history",
    insurance: "Travel insurance",
    invitation: "Host invitation",
    interview: "Interview readiness",
    passport: "Passport nationality",
    health_character: "Health and character",
    return_ticket: "Return ticket",
    itinerary: "Travel itinerary",
    prior_refusals: "Prior visa history",
  };
  return map[id] || id;
};

/* ─── Country search dropdown (shared) ─── */
function CountrySearch({ value, onChange, onSelect, placeholder = "Search destination country...", filtered, showDropdown, setShowDropdown }) {
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [setShowDropdown]);

  return (
    <div className="vcheck-search-wrap" ref={wrapRef}>
      <div className="vcheck-search-inner">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          className="vcheck-search-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
        />
        {value && (
          <button className="vcheck-clear-btn" onClick={() => { onChange(""); onSelect(null); }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
      {showDropdown && filtered.length > 0 && (
        <div className="vcheck-dropdown">
          {filtered.map((c) => (
            <button key={c.code} className="vcheck-dropdown-item" onClick={() => { onSelect(c); setShowDropdown(false); }}>
              <span className="vcheck-dropdown-name">{c.name}</span>
              <span className="vcheck-dropdown-meta">{c.region} — {c.visaType}</span>
            </button>
          ))}
        </div>
      )}
      {showDropdown && filtered.length === 0 && value && (
        <div className="vcheck-dropdown">
          <div className="vcheck-dropdown-empty">No matching countries found</div>
        </div>
      )}
    </div>
  );
}

/* ─── PassportSelect (shared) ─── */
function PassportSelect({ value, onChange, error, className = "" }) {
  return (
    <div className={`vcheck-field ${className}`}>
      <label className="vcheck-label">Passport Holding Country</label>
      <select
        className={`vcheck-input${error ? " vcheck-input--err" : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select your passport country</option>
        {passportCountries.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      {error && <span className="vcheck-err">{error}</span>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   ESTIMATOR (Visa Approval Estimator)
══════════════════════════════════════════════ */
function VisaEstimator() {
  const [step, setStep] = React.useState("search");
  const [search, setSearch] = React.useState("");
  const [region, setRegion] = React.useState("All Regions");
  const [visaType, setVisaType] = React.useState("All visa types");
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [fullName, setFullName] = React.useState("");
  const [passportCountry, setPassportCountry] = React.useState("");
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [qIndex, setQIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState({});
  const [result, setResult] = React.useState(null);
  const [nameError, setNameError] = React.useState("");
  const [passportError, setPassportError] = React.useState("");

  const filtered = React.useMemo(() => {
    return visaCountries.filter((c) => {
      const matchSearch = matchesCountryString(c.name, search);
      const matchRegion = region === "All Regions" || c.region === region;
      return matchSearch && matchRegion;
    });
  }, [search, region]);

  const handleBegin = () => {
    let valid = true;
    if (!fullName.trim()) { setNameError("Please enter your full name."); valid = false; }
    else setNameError("");
    if (!passportCountry) { setPassportError("Please select your passport country."); valid = false; }
    else setPassportError("");
    if (!valid) return;
    setStep("questions");
    setQIndex(0);
    setAnswers({});
  };

  const pickCountry = (c) => {
    setSelectedCountry(c);
    setSearch(c ? c.name : "");
  };

  const selectOption = (qId, idx) => {
    setAnswers((prev) => ({ ...prev, [qId]: idx }));
  };

  const questions = selectedCountry ? selectedCountry.questions : [];
  const currentQ = questions[qIndex];
  const currentAnswer = currentQ ? answers[currentQ.id] : undefined;

  const goNext = () => {
    if (currentAnswer === undefined) return;
    if (qIndex < questions.length - 1) setQIndex(qIndex + 1);
    else calculateResult();
  };

  const goPrev = () => { if (qIndex > 0) setQIndex(qIndex - 1); };

  const calculateResult = () => {
    const country = selectedCountry;
    let totalScore = 0;
    let factors = [];
    let improvementNotes = [];

    country.questions.forEach((q) => {
      const answerIdx = answers[q.id];
      if (answerIdx === undefined) return;
      const option = q.options[answerIdx];
      const score = option.score;
      const maxScore = Math.max(...q.options.map((o) => o.score));
      const ratio = maxScore > 0 ? score / maxScore : 0;
      totalScore += score;
      factors.push({ label: getFactorLabel(q.id), score: Math.round(ratio * 10 * 10) / 10, maxScore: 10, ratio });
      if (ratio < 0.8 && q.improvementNote) improvementNotes.push({ area: getFactorLabel(q.id), note: q.improvementNote });
    });

    const pct = Math.min(Math.round((totalScore / country.maxScore) * 100), 99);
    let verdict, verdictClass;
    if (pct >= 65) { verdict = "Likely Approved"; verdictClass = "high"; }
    else if (pct >= 40) { verdict = "Borderline — Strengthen Application"; verdictClass = "medium"; }
    else { verdict = "High Risk of Refusal"; verdictClass = "low"; }

    setResult({ pct, verdict, verdictClass, factors, improvementNotes });
    setStep("result");
  };

  const reset = () => {
    setStep("search"); setSearch(""); setSelectedCountry(null); setFullName("");
    setPassportCountry(""); setAnswers({}); setResult(null); setQIndex(0);
    setNameError(""); setPassportError(""); setRegion("All Regions"); setVisaType("All visa types");
  };

  return (
    <div className="vtool-panel">
      {step === "search" && (
        <div className="vcheck-card" data-aos="fade-up" data-aos-duration="700" data-aos-delay="80">
          <div className="vcheck-step-label">Step 01 — Destination and Details</div>

          <div className="vcheck-search-row">
            <CountrySearch
              value={search}
              onChange={(v) => { setSearch(v); setSelectedCountry(null); }}
              onSelect={pickCountry}
              filtered={filtered}
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
            />
            <select className="vcheck-select" value={visaType} onChange={(e) => setVisaType(e.target.value)}>
              {visaTypeList.map((v) => <option key={v}>{v}</option>)}
            </select>
          </div>

          <div className="vcheck-filter-bar">
            {regionList.map((r) => (
              <button key={r} className={`vcheck-filter-pill${region === r ? " vcheck-filter-pill--active" : ""}`} onClick={() => setRegion(r)}>
                {r}
              </button>
            ))}
          </div>

          {selectedCountry && (
            <div className="vcheck-selected-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{selectedCountry.name} — {selectedCountry.visaType} — {selectedCountry.questions.length} questions</span>
            </div>
          )}

          <div className="vcheck-details-row">
            <div className="vcheck-2col-fields">
              <div className="vcheck-field">
                <label className="vcheck-label">Your Full Name</label>
                <input
                  type="text"
                  className={`vcheck-input${nameError ? " vcheck-input--err" : ""}`}
                  placeholder="As it appears on your passport"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); setNameError(""); }}
                />
                {nameError && <span className="vcheck-err">{nameError}</span>}
              </div>
              <PassportSelect value={passportCountry} onChange={(v) => { setPassportCountry(v); setPassportError(""); }} error={passportError} />
            </div>
            <button className="vcheck-btn-primary" onClick={handleBegin} disabled={!selectedCountry}>
              Begin Assessment
            </button>
          </div>
        </div>
      )}

      {step === "questions" && selectedCountry && currentQ && (
        <div className="vcheck-card vcheck-card--questions" data-aos="fade-up" data-aos-duration="600">
          <div className="vcheck-q-header">
            <div className="vcheck-country-badge">{selectedCountry.name} — {selectedCountry.visaType}</div>
            <div className="vcheck-progress-wrap">
              <div className="vcheck-progress-label">
                <span>Progress</span>
                <span>{qIndex + 1} of {questions.length}</span>
              </div>
              <div className="vcheck-progress-bar">
                <div className="vcheck-progress-fill" style={{ "--progress-w": `${((qIndex + 1) / questions.length) * 100}%` }} />
              </div>
            </div>
          </div>

          <div className="vcheck-q-num">Question {pad2(qIndex + 1)} of {pad2(questions.length)}</div>
          <div className="vcheck-q-text">{currentQ.text}</div>

          <div className="vcheck-options">
            {currentQ.options.map((opt, idx) => (
              <button key={idx} className={`vcheck-option${currentAnswer === idx ? " vcheck-option--selected" : ""}`} onClick={() => selectOption(currentQ.id, idx)}>
                <div className="vcheck-option-dot">
                  {currentAnswer === idx && <div className="vcheck-option-dot-inner" />}
                </div>
                <div>
                  <div className="vcheck-option-label">{opt.label}</div>
                  {opt.note && <div className="vcheck-option-note">{opt.note}</div>}
                </div>
              </button>
            ))}
          </div>

          <div className="vcheck-q-nav">
            <button className="vcheck-btn-ghost" onClick={goPrev} disabled={qIndex === 0}>Previous</button>
            <button className="vcheck-btn-primary" onClick={goNext} disabled={currentAnswer === undefined}>
              {qIndex === questions.length - 1 ? "View Results" : "Next Question"}
            </button>
          </div>
        </div>
      )}

      {step === "result" && result && selectedCountry && (
        <div className="vcheck-card vcheck-card--result" data-aos="fade-up" data-aos-duration="700">
          <div className="vcheck-result-header">
            <div className="vcheck-result-info">
              <div className="vcheck-result-meta-label">Assessment result for</div>
              <div className="vcheck-result-name">{fullName}</div>
              <div className="vcheck-result-country">{selectedCountry.name} — {selectedCountry.visaType}</div>
              <div className={`vcheck-verdict vcheck-verdict--${result.verdictClass}`}>{result.verdict}</div>
            </div>
            <div className="vcheck-meter-wrap">
              <div className="vcheck-meter-circle">
                <svg className="vcheck-meter-svg" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="var(--border-light)" strokeWidth="9" />
                  <circle cx="60" cy="60" r="54" fill="none" strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={CIRC} strokeDashoffset={getMeterOffset(result.pct)} className="vcheck-meter-stroke" />
                </svg>
                <div className="vcheck-meter-val">
                  <div className="vcheck-meter-pct">{result.pct}%</div>
                  <div className="vcheck-meter-sub">Approval</div>
                </div>
              </div>
            </div>
          </div>

          <div className="vcheck-breakdown">
            <div className="vcheck-breakdown-title">Factor Breakdown</div>
            {result.factors.map((f, i) => (
              <div key={i} className="vcheck-factor-row">
                <div className="vcheck-factor-label">{f.label}</div>
                <div className="vcheck-factor-bar-wrap">
                  <div className="vcheck-factor-bar">
                    <div className="vcheck-factor-bar-fill" style={{ "--bar-w": `${f.ratio * 100}%`, "--bar-color": f.ratio >= 0.8 ? "#10b981" : f.ratio >= 0.5 ? "#d97706" : "#ef4444" }} />
                  </div>
                </div>
                <div className="vcheck-factor-score">{f.score}/10</div>
              </div>
            ))}
          </div>

          <div className="vcheck-divider" />

          {result.improvementNotes.length > 0 ? (
            <div className="vcheck-notes">
              <div className="vcheck-notes-title">Areas for Improvement</div>
              {result.improvementNotes.map((n, i) => (
                <div key={i} className="vcheck-note-item">
                  <div className="vcheck-note-index">{pad2(i + 1)}</div>
                  <div className="vcheck-note-body">
                    <div className="vcheck-note-area">{n.area}</div>
                    <div className="vcheck-note-text">{n.note}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="vcheck-notes">
              <div className="vcheck-notes-title">Assessment Summary</div>
              <div className="vcheck-note-item">
                <div className="vcheck-note-index">01</div>
                <div className="vcheck-note-body">
                  <div className="vcheck-note-area">Strong Profile</div>
                  <div className="vcheck-note-text">Your answers across all assessed factors indicate a well-prepared application. Ensure all supporting documents are current, certified where required, and submitted in the order specified by the embassy. A cover letter summarising your itinerary and financial position is recommended even when not mandatory.</div>
                </div>
              </div>
            </div>
          )}

          <div className="vcheck-reset-row">
            <button className="vcheck-reset-btn" onClick={reset}>Start a new assessment</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   VALIDATOR (Travel Plan Validator)
══════════════════════════════════════════════ */
function TravelValidator() {
  const [search, setSearch] = React.useState("");
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [passportCountry, setPassportCountry] = React.useState("");
  const [passportNumber, setPassportNumber] = React.useState("");
  const [passportExpiry, setPassportExpiry] = React.useState("");
  const [travelDate, setTravelDate] = React.useState("");
  const [returnDate, setReturnDate] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [errors, setErrors] = React.useState({});

  const filtered = React.useMemo(() => {
    return visaCountries.filter((c) => matchesCountryString(c.name, search));
  }, [search]);

  const pickCountry = (c) => {
    setSelectedCountry(c);
    setSearch(c ? c.name : "");
  };

  const validate = () => {
    const errs = {};
    if (!selectedCountry) errs.country = "Please select a destination country.";
    if (!passportCountry) errs.passport = "Please select your passport country.";
    if (!passportNumber.trim()) errs.passportNumber = "Please enter your passport number.";
    if (!passportExpiry) errs.passportExpiry = "Please enter your passport expiry date.";
    if (!travelDate) errs.travelDate = "Please enter your planned travel date.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const checkValidity = () => {
    if (!validate()) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(passportExpiry);
    const tripStart = new Date(travelDate);
    const tripEnd = returnDate ? new Date(returnDate) : null;

    const rule = passportValidityRules[selectedCountry.code] || { minMonthsBeyond: 6, note: "Generally must be valid for 6 months beyond your stay." };

    const referenceDate = tripEnd || tripStart;
    const requiredExpiry = new Date(referenceDate);
    requiredExpiry.setMonth(requiredExpiry.getMonth() + rule.minMonthsBeyond);

    const msPerDay = 86400000;
    const daysToTravelStart = Math.round((tripStart - today) / msPerDay);
    const daysPassportValidFromToday = Math.round((expiryDate - today) / msPerDay);
    const daysPassportValidBeyondTrip = Math.round((expiryDate - (tripEnd || tripStart)) / msPerDay);
    const tripLengthDays = tripEnd ? Math.round((tripEnd - tripStart) / msPerDay) : null;

    const passportExpiredAlready = expiryDate < today;
    const passportValidOnTravel = expiryDate >= tripStart;
    const meetsDestinationRule = expiryDate >= requiredExpiry;

    let status, recommendation, canRenewAfter;
    if (passportExpiredAlready) {
      status = "expired";
      recommendation = "Your passport has already expired. You must renew it before you can travel.";
      canRenewAfter = false;
    } else if (!passportValidOnTravel) {
      status = "invalid_on_travel";
      recommendation = "Your passport will expire before your travel date. You must renew it before your trip.";
      canRenewAfter = false;
    } else if (!meetsDestinationRule) {
      if (tripEnd) {
        status = "needs_renewal_before";
        recommendation = `${selectedCountry.name} requires your passport to be valid for at least ${rule.minMonthsBeyond} month${rule.minMonthsBeyond !== 1 ? "s" : ""} beyond your departure. Your passport expires before this threshold. You must renew your passport before travelling.`;
        canRenewAfter = false;
      } else {
        status = "needs_renewal_before";
        recommendation = `${selectedCountry.name} requires your passport to be valid for at least ${rule.minMonthsBeyond} month${rule.minMonthsBeyond !== 1 ? "s" : ""} beyond your stay. You must renew before travelling.`;
        canRenewAfter = false;
      }
    } else {
      status = "valid";
      const sixMonthsAfterTrip = new Date(tripEnd || tripStart);
      sixMonthsAfterTrip.setMonth(sixMonthsAfterTrip.getMonth() + 6);
      if (expiryDate < sixMonthsAfterTrip) {
        canRenewAfter = true;
        recommendation = `Your passport is valid for this trip. However, after returning you will have limited validity remaining (expires ${expiryDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}). You can renew after your trip.`;
      } else {
        canRenewAfter = true;
        recommendation = `Your passport is valid for this trip and meets all requirements for ${selectedCountry.name}. No action needed before travel.`;
      }
    }

    setResult({ status, recommendation, canRenewAfter, daysToTravelStart, daysPassportValidFromToday, daysPassportValidBeyondTrip, tripLengthDays, expiryDate, requiredExpiry, rule, meetsDestinationRule, passportExpiredAlready });
  };

  const reset = () => {
    setSearch(""); setSelectedCountry(null); setPassportCountry("");
    setPassportNumber(""); setPassportExpiry(""); setTravelDate("");
    setReturnDate(""); setResult(null); setErrors({});
  };

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div className="vtool-panel">
      {!result ? (
        <div className="vcheck-card" data-aos="fade-up" data-aos-duration="700" data-aos-delay="80">
          <div className="vcheck-step-label">Travel Plan Validator</div>

          <div className="vtool-grid">
            <div className="vtool-col">
              <div className="vtool-col-head">
                <span className="vtool-col-label">Passport Details</span>
              </div>

              <PassportSelect value={passportCountry} onChange={setPassportCountry} error={errors.passport} />

              <div className="vcheck-field" style={{ marginTop: "1rem" }}>
                <label className="vcheck-label">Passport Number</label>
                <input
                  type="text"
                  className={`vcheck-input${errors.passportNumber ? " vcheck-input--err" : ""}`}
                  placeholder="e.g. A1234567"
                  value={passportNumber}
                  onChange={(e) => { setPassportNumber(e.target.value.toUpperCase()); setErrors((p) => ({ ...p, passportNumber: "" })); }}
                />
                {errors.passportNumber && <span className="vcheck-err">{errors.passportNumber}</span>}
              </div>

              <div className="vcheck-field" style={{ marginTop: "1rem" }}>
                <label className="vcheck-label">Passport Expiry Date</label>
                <input
                  type="date"
                  className={`vcheck-input${errors.passportExpiry ? " vcheck-input--err" : ""}`}
                  value={passportExpiry}
                  min={todayStr}
                  onChange={(e) => { setPassportExpiry(e.target.value); setErrors((p) => ({ ...p, passportExpiry: "" })); }}
                />
                {errors.passportExpiry && <span className="vcheck-err">{errors.passportExpiry}</span>}
              </div>
            </div>

            <div className="vtool-col">
              <div className="vtool-col-head">
                <span className="vtool-col-label">Trip Details</span>
              </div>

              <div className="vcheck-field">
                <label className="vcheck-label">Destination Country</label>
                <CountrySearch
                  value={search}
                  onChange={(v) => { setSearch(v); setSelectedCountry(null); setErrors((p) => ({ ...p, country: "" })); }}
                  onSelect={pickCountry}
                  filtered={filtered}
                  showDropdown={showDropdown}
                  setShowDropdown={setShowDropdown}
                  placeholder="Search destination..."
                />
                {errors.country && <span className="vcheck-err" style={{ marginTop: ".35rem" }}>{errors.country}</span>}
                {selectedCountry && (
                  <div className="vcheck-selected-badge" style={{ marginTop: ".6rem" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{selectedCountry.name} — {selectedCountry.visaType}</span>
                  </div>
                )}
              </div>

              <div className="vcheck-field" style={{ marginTop: "1rem" }}>
                <label className="vcheck-label">Planned Travel Date <span className="vtool-req-star">*</span></label>
                <input
                  type="date"
                  className={`vcheck-input${errors.travelDate ? " vcheck-input--err" : ""}`}
                  value={travelDate}
                  min={todayStr}
                  onChange={(e) => { setTravelDate(e.target.value); setErrors((p) => ({ ...p, travelDate: "" })); }}
                />
                {errors.travelDate && <span className="vcheck-err">{errors.travelDate}</span>}
              </div>

              <div className="vcheck-field" style={{ marginTop: "1rem" }}>
                <label className="vcheck-label">
                  Return Date <span className="vtool-optional-tag">Optional</span>
                </label>
                <input
                  type="date"
                  className="vcheck-input"
                  value={returnDate}
                  min={travelDate || todayStr}
                  onChange={(e) => setReturnDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="vtool-actions">
            <button className="vcheck-btn-primary" onClick={checkValidity}>
              Validate Travel Plan
            </button>
          </div>
        </div>
      ) : (
        <div className="vcheck-card vcheck-card--result">
          <div className="vtool-result-header">
            <div className={`vtool-status-icon vtool-status-icon--${result.status === "valid" ? "ok" : "warn"}`}>
              {result.status === "valid" ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}
            </div>
            <div className="vtool-result-info">
              <div className="vcheck-result-meta-label">Validation result for passport {passportNumber}</div>
              <div className="vcheck-result-name">{passportCountry}  {selectedCountry.name}</div>
              <div className="vcheck-result-country">{selectedCountry.visaType}</div>
              <div className={`vcheck-verdict ${result.status === "valid" ? "vcheck-verdict--high" : "vcheck-verdict--low"}`}>
                {result.status === "valid" ? "Passport Valid for Trip" : "Action Required"}
              </div>
            </div>
          </div>

          <div className="vtool-stats-grid">
            <div className="vtool-stat-card">
              <div className="vtool-stat-label">Days Until Travel</div>
              <div className="vtool-stat-value">{result.daysToTravelStart < 0 ? "In past" : result.daysToTravelStart}</div>
            </div>
            <div className="vtool-stat-card">
              <div className="vtool-stat-label">Passport Valid (days)</div>
              <div className="vtool-stat-value" style={{ color: result.daysPassportValidFromToday < 180 ? "#ef4444" : "#10b981" }}>
                {result.passportExpiredAlready ? "Expired" : result.daysPassportValidFromToday}
              </div>
            </div>
            {result.tripLengthDays !== null && (
              <div className="vtool-stat-card">
                <div className="vtool-stat-label">Trip Duration (days)</div>
                <div className="vtool-stat-value">{result.tripLengthDays}</div>
              </div>
            )}
            <div className="vtool-stat-card">
              <div className="vtool-stat-label">Min. Validity Required</div>
              <div className="vtool-stat-value">{result.rule.minMonthsBeyond} Month</div>
            </div>
          </div>

          <div className="vcheck-divider" />

          <div className="vcheck-notes">
            <div className="vcheck-notes-title">Recommendation</div>
            <div className="vcheck-note-item">
              <div className={`vcheck-note-index ${result.status === "valid" ? "vtool-ok-index" : "vtool-warn-index"}`}>
                {result.status === "valid" ? "OK" : "!"}
              </div>
              <div className="vcheck-note-body">
                <div className="vcheck-note-area">
                  {result.status === "valid" ? "You are good to travel" : "Passport renewal required"}
                </div>
                <div className="vcheck-note-text">{result.recommendation}</div>
              </div>
            </div>
            <div className="vcheck-note-item">
              <div className="vcheck-note-index">ℹ</div>
              <div className="vcheck-note-body">
                <div className="vcheck-note-area">{selectedCountry.name} — Passport Rule</div>
                <div className="vcheck-note-text">{result.rule.note}</div>
              </div>
            </div>
            {result.status === "valid" && result.canRenewAfter && (
              <div className="vcheck-note-item">
                <div className="vcheck-note-index" style={{ background: "rgba(16,185,129,.1)", color: "#065f46" }}></div>
                <div className="vcheck-note-body">
                  <div className="vcheck-note-area">Passport Renewal Timing</div>
                  <div className="vcheck-note-text">
                    You have the flexibility to renew your passport after completing this trip. Your current passport expires on {result.expiryDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}. We recommend initiating renewal within 3 months of your return.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="vcheck-reset-row">
            <button className="vcheck-reset-btn" onClick={reset}>Validate another trip</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN COMPONENT — toggle between tools
══════════════════════════════════════════════ */
export default function VisaChecker() {
  const [activeTool, setActiveTool] = React.useState("estimator");

  return (
    <section className="visa-checker" id="visa-checker">
      <div className="visa-checker__inner">

        {/* ── SECTION HEADER ── */}
        <div className="visa-checker__head" data-aos="fade-up" data-aos-duration="700">
          <h2 className="visa-checker__title">Visa Intelligence Suite</h2>
          <p className="visa-checker__sub">
            Estimate your visa approval probability or validate whether your Passport/Visa is ready for your next trip — all in one place.
          </p>
        </div>

        {/* ══ 2-COLUMN GRID: speedometer left, tools right ══ */}
        <div className="visa-checker__grid" data-aos="fade-up" data-aos-duration="800" data-aos-delay="80">

          {/* ── LEFT COLUMN: Speedometer Gauge ── */}
          <div className="visa-checker__grid-left">
            <SpeedometerGauge targetValue={80} />
          </div>

          {/* ── RIGHT COLUMN: Tab toggle + active panel ── */}
          <div className="visa-checker__grid-right">
            <div className="vtool-toggle-wrap">
              <div className="vtool-toggle">
                <button
                  className={`vtool-toggle-btn${activeTool === "estimator" ? " vtool-toggle-btn--active" : ""}`}
                  onClick={() => setActiveTool("estimator")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  Visa-Secure
                </button>
                <button
                  className={`vtool-toggle-btn${activeTool === "validator" ? " vtool-toggle-btn--active" : ""}`}
                  onClick={() => setActiveTool("validator")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="16" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                  Doc-Checker
                </button>
              </div>
            </div>

            {/* ── ACTIVE PANEL ── */}
            {activeTool === "estimator" ? <VisaEstimator /> : <TravelValidator />}
          </div>

        </div>

      </div>
    </section>
  );
}